import SiteConfig from '../model/SiteConfig'
import { configuration } from './Configuration'
import fetch from 'isomorphic-fetch'
import { getAccessToken } from '../auth/authProvider'

export const createSolarGisSite = (siteApiKey: string, xml: string): Promise<string> => {
  return getAccessToken('SolarGis', "")
    .then(function (accessToken) {
      var headers = new Headers()
      var bearer = 'Bearer ' + accessToken
      headers.append('Authorization', bearer)
      var options = {
        method: 'POST',
        headers: headers,
        body: xml
      }
      return fetchFromURL<string>(`${configuration.SolarGisPvSpotAPI}api/${siteApiKey}`, options)
    })
    .catch(function (error) {
      console.table(error)
      return error
    })
}

const getSolarGisSites = (): Promise<string[] | null> => {
  return getAccessToken('SolarGis', "")
    .then(function (accessToken) {
      var headers = new Headers()
      var bearer = 'Bearer ' + accessToken
      headers.append('Authorization', bearer)
      var options = {
        method: 'GET',
        headers: headers
      }
      return fetchFromURL<string[]>(`${configuration.SolarGisPvSpotAPI}api/sites`, options)
    })
    .catch(function (error) {
      console.table(error)
      return null
    })
}

export type SitesLoadData = {
  sites: SiteConfig[];
}

export const loadSiteConfigList = (market: string): Promise<SitesLoadData> =>
  Promise.all([
    fetchFromURLWithAccessToken<SiteConfig[]>(`${configuration.SmartAPI}sites?market=${market}`, 'SmartApi'),
    getSolarGisSites()
  ]).then(results => {
    const sites = results[0]
    const solarGisSites = results[1]
    if (sites === null) {
      return { sites: new Array<SiteConfig>() };
    } else if (solarGisSites == null) {
      return { sites }
    } else {
      return {
        sites: sites.map(siteConfig => ({
          ...siteConfig,
          solarGisEnabled: solarGisSites.indexOf(siteConfig.key) >= 0
        }))
      }
    }
  })

export const loadSiteConfig = (apiKey: string): Promise<SiteConfig> =>
fetchFromURLWithAccessToken<SiteConfig>(`${configuration.SmartAPI}siteConfig\\${apiKey}`, 'SmartApi')
    .then((siteConfig: SiteConfig) => {
      return siteConfig;
    });

export const fetchWithAccessToken = (url: string, api: 'SolarGis' | 'DowntimeApi' | 'SmartApi', options: any = {}, marketKey: string = "") : Promise<Response> => {
  return getAccessToken(api, marketKey)
  .then(function (accessToken) {
    var headers = new Headers()
    var bearer = 'Bearer ' + accessToken
    headers.append('Authorization', bearer)
    options.headers = headers
    return fetch(url, options)
  })
}

export const fetchFromURLWithAccessToken = <T>(url: string, api: 'SolarGis' | 'DowntimeApi' | 'SmartApi', marketKey: string = ""): Promise<T> =>
    getAccessToken(api, marketKey)
      .then(function (accessToken) {
        var headers = new Headers()
        var bearer = 'Bearer ' + accessToken
        headers.append('Authorization', bearer)
        var options = {
          method: 'GET',
          headers: headers
        }
        return fetchFromURL<T>(url, options)
      })

const fetchFromURL = <T>(url: string, options: any = null): Promise<T> =>
  fetch(url, options)
    .then(function (response: Response) {
      if (response.status !== 200) {
        console.log('failed to fetch ' + url + ' because: ' + response.statusText)
        const error = response.statusText ? response.statusText : 'Error code: ' + response.status;
        throw error
      }
      // Convert to JSON
      return response.json()
    })
    .catch(reason => {
      console.log('failed to fetch ' + url + ' because: ' + reason)
      throw reason
    })

export const saveSiteConfig = (siteConfig: SiteConfig): Promise<void> =>
  saveSiteFromURL(`${configuration.SmartAPI}siteconfig`, siteConfig)

const saveSiteFromURL = (url: string, siteConfig: SiteConfig): Promise<void> => {
  var json = JSON.stringify(siteConfig)
  return fetchWithAccessToken(url, 'SmartApi', {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf-8',
      'Access-Control-Allow-Credentials': 'true'
    },
    body: json
  })
    .then((response: Response) => {
      if (response.status !== 200) {
        return response.json()
      }
      return null
    })
    .then((error: { message: string }) => {
      if (error !== null) {
        throw error
      }
    })
}

const hasAuth = (api: 'SolarGis' | 'DowntimeApi' | 'SmartApi', marketKey: string): boolean => {
  switch (api)
  {
    case 'SolarGis': return true;
    case 'DowntimeApi': return (configuration.DowntimeAPIScope(marketKey.toLowerCase().trim())?.length ?? 0) > 0;
    case 'SmartApi': return (configuration.SmartAPIScope?.length ?? 0) > 0
  }
}

export const addAuthHeader = async (api: 'SolarGis' | 'DowntimeApi' | 'SmartApi', marketKey: string): Promise<object> => {
  var accessToken = "";
  if (hasAuth(api, marketKey)) {
    accessToken = await getAccessToken(api, marketKey);
  }
  return (accessToken !== "")
    ? { "Authorization": `Bearer ${accessToken}` }
    : {};
}
